import {
	type BuildGuideDictionaryKey,
	type BuildGuideStepModifier,
	type BuildGuideSubstepDraft,
	type BuildGuideText,
	buildGuideText,
	type ProjectBuildGuideStepDraft,
} from "../domain/build/buildGuideDraft";
import { createBuildGuideSubstepId } from "../domain/build/buildProgress";
import { getT } from "../i18n";
import { INDOOR_FIBREBOARD_KEY, INDOOR_OUTER_PANEL_KEY } from "./squareSauna";

/** Steps an indoor sauna (issue 156) never builds: it stands on the existing floor. */
const INDOOR_SKIPPED_STEP_IDS = new Set(["foundation"]);

/** The roof substeps a flat ceiling under the house's own roof never needs. */
const OUTDOOR_ROOF_CHECKPOINT_IDS = new Set([
	"roof-weather",
	"roof-gable-infill",
]);

/**
 * Facade substeps that belong to an outdoor facade: the ventilated cladding
 * (replaced below) and the exterior treatment (indoors nothing is weathered).
 * The door substep stays — the door is hung the same way indoors.
 */
const OUTDOOR_FACADE_CHECKPOINT_IDS = new Set([
	"facade-air-gap",
	"facade-front-wall",
	"facade-treatment-coverage",
	"facade-treatment-drying",
]);

const hasCheckpointIn = (substep: BuildGuideSubstepDraft, ids: Set<string>) =>
	substep.checkpoints.some((checkpoint) => ids.has(checkpoint.id));

function renumber(
	stepId: string,
	substeps: BuildGuideSubstepDraft[],
): BuildGuideSubstepDraft[] {
	return substeps.map((substep, index) => ({
		...substep,
		id: createBuildGuideSubstepId(stepId, index),
	}));
}

/**
 * Issue 175: the ceiling joists the indoor sauna does build, described as a
 * ceiling — not as a roof to close against the weather.
 */
function toIndoorCeiling(
	step: ProjectBuildGuideStepDraft,
): ProjectBuildGuideStepDraft {
	return {
		...step,
		title: buildGuideText("domain_build_indoorCeilingTitle"),
		purpose: buildGuideText("domain_build_guide_indoorCeilingPurpose"),
		prerequisites: [buildGuideText("domain_build_guide_indoorCeilingPrereq")],
		substeps: renumber(
			step.id,
			step.substeps
				.filter(
					(substep) => !hasCheckpointIn(substep, OUTDOOR_ROOF_CHECKPOINT_IDS),
				)
				.map((substep) => ({
					...substep,
					title: buildGuideText("domain_build_guide_indoorCeilingSubstep"),
					checkpoints: [
						{
							id: "indoor-ceiling-joists",
							label: buildGuideText(
								"domain_build_guide_indoorCeilingCheckpoint",
							),
						},
					],
				})),
		),
		materialsSummary: [],
		risks: [buildGuideText("domain_build_guide_indoorCeilingRisk")],
		controls: [buildGuideText("domain_build_guide_indoorCeilingControl")],
	};
}

/**
 * Issue 174/175: a wall that will stand against the room's wall gets its
 * träfiberskiva FIRST, while it still lies on the floor — once it is raised
 * the 20 mm gap behind it cannot be reached. So it opens the framing step.
 */
function withFibreboardFirst(
	step: ProjectBuildGuideStepDraft,
): ProjectBuildGuideStepDraft {
	const fibreboard: BuildGuideSubstepDraft = {
		id: "",
		title: buildGuideText("domain_build_guide_indoorFibreboardSubstep"),
		mistakeCallout: {
			severity: "quality",
			wrong: buildGuideText("domain_build_guide_indoorFibreboardWrong"),
			right: buildGuideText("domain_build_guide_indoorFibreboardRight"),
		},
		checkpoints: [
			{
				id: "indoor-fibreboard-gap",
				label: buildGuideText("domain_build_guide_indoorFibreboardCheckpoint"),
			},
		],
	};
	return {
		...step,
		substeps: renumber(step.id, [fibreboard, ...step.substeps]),
	};
}

/**
 * Issue 174/175: the sauna's outside as seen from the room. Free walls get
 * panel and foder straight on the studs; only bought when some wall is free.
 */
function toIndoorOuterWalls(
	step: ProjectBuildGuideStepDraft,
	buys: { outerPanel: boolean },
): ProjectBuildGuideStepDraft {
	const indoorSubsteps: BuildGuideSubstepDraft[] = [
		...(buys.outerPanel
			? [
					{
						id: "",
						title: buildGuideText("domain_build_guide_indoorOuterPanelSubstep"),
						checkpoints: [
							{
								id: "indoor-outer-panel",
								label: buildGuideText(
									"domain_build_guide_indoorOuterPanelCheckpoint",
								),
							},
						],
					},
				]
			: []),
	];
	const kept = step.substeps.filter(
		(substep) => !hasCheckpointIn(substep, OUTDOOR_FACADE_CHECKPOINT_IDS),
	);
	return {
		...step,
		title: buildGuideText("domain_build_indoorOuterTitle"),
		purpose: buildGuideText("domain_build_guide_indoorOuterPurpose"),
		prerequisites: [buildGuideText("domain_build_guide_indoorOuterPrereq")],
		substeps: renumber(step.id, [...indoorSubsteps, ...kept]),
		materialsSummary: [],
		risks: [],
		controls: [buildGuideText("domain_build_guide_indoorOuterControl")],
	};
}

/**
 * Shared texts that assume a plot, a foundation or an outdoor wall, and the
 * indoor wording that replaces them wherever they appear in a step.
 */
const INDOOR_TEXT: Partial<
	Record<BuildGuideDictionaryKey, BuildGuideDictionaryKey>
> = {
	domain_build_guide_drainagePrereq: "domain_build_guide_indoorDrainagePrereq",
	domain_build_guide_drainageControlGround:
		"domain_build_guide_indoorDrainageControl",
	domain_build_guide_framingPrereq: "domain_build_guide_indoorFramingPrereq",
	domain_build_guide_drainageCheckpointGroundDrain:
		"domain_build_guide_indoorDrainageControl",
	domain_build_constructionMoisturePrereq:
		"domain_build_guide_indoorMoisturePrereq",
};

function toIndoorText(text: BuildGuideText): BuildGuideText {
	if (typeof text === "string" || !("key" in text)) return text;
	const indoorKey = INDOOR_TEXT[text.key];
	return indoorKey ? { ...text, key: indoorKey } : text;
}

/**
 * The shared guide, re-described for a sauna inside an existing building.
 *
 * Drops the foundation step. Framing opens with the träfiberskiva on walls
 * against the room, the roof step becomes the ceiling joists, and the facade
 * step becomes the panel on the free walls (the door substep stays). Gates that were anchored on a dropped step
 * still show — `insertGatesBeforeSteps` moves them to the front — because an
 * eldstadsanmälan is required indoors too.
 */
export const modifyIndoorBuildGuideSteps: BuildGuideStepModifier = (
	steps,
	{ data, market },
) => {
	const buys = {
		fibreboard: data.materials.some(
			(row) => row.materialKey === INDOOR_FIBREBOARD_KEY,
		),
		outerPanel: data.materials.some(
			(row) => row.materialKey === INDOOR_OUTER_PANEL_KEY,
		),
	};
	return steps
		.filter((step) => !INDOOR_SKIPPED_STEP_IDS.has(step.id))
		.map((step): ProjectBuildGuideStepDraft => {
			const next = {
				...step,
				dependencyIds: step.dependencyIds.filter(
					(id) => !INDOOR_SKIPPED_STEP_IDS.has(id),
				),
				prerequisites: step.prerequisites.map(toIndoorText),
				controls: step.controls.map(toIndoorText),
				risks: step.risks.map(toIndoorText),
				substeps: step.substeps.map((substep) => ({
					...substep,
					checkpoints: substep.checkpoints.map((checkpoint) => ({
						...checkpoint,
						label: toIndoorText(checkpoint.label),
					})),
				})),
			};
			if (step.id === "framing" && buys.fibreboard)
				return withFibreboardFirst(next);
			if (step.id === "roof") return toIndoorCeiling(next);
			if (step.id === "facade")
				return toIndoorOuterWalls(
					{
						...next,
						// The shared timeline names the step "Fasad & ytterpanel".
						timeline: next.timeline && {
							...next.timeline,
							label: getT(market).domain_build_indoorOuterTitle,
						},
					},
					buys,
				);
			return next;
		});
};
